/**
 * Studio Intelligence Hub
 * Main analytics dashboard for game studios
 * Solis Games - Phase 2.2 Implementation
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../utils/supabase';
import { studioIntelligenceService } from '../../utils/studioIntelligenceService';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import CohortRetentionHeatMap from './components/CohortRetentionHeatMap';
import CompetitiveBenchmarkChart from './components/CompetitiveBenchmarkChart';
import PlayerJourneyFunnel from './components/PlayerJourneyFunnel';
import PeakHoursHeatMap from './components/PeakHoursHeatMap';

export default function StudioIntelligenceHub() {
  const { studioId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();

  // State
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedGame, setSelectedGame] = useState(null);
  const [dateRange, setDateRange] = useState(30); // Days back
  const [studioGames, setStudioGames] = useState([]);

  // Analytics data
  const [cohortData, setCohortData] = useState(null);
  const [benchmarkData, setBenchmarkData] = useState(null);
  const [journeyData, setJourneyData] = useState(null);
  const [peakHoursData, setPeakHoursData] = useState(null);

  // Loading states
  const [loadingCohort, setLoadingCohort] = useState(false);
  const [loadingBenchmark, setLoadingBenchmark] = useState(false);
  const [loadingJourney, setLoadingJourney] = useState(false);
  const [loadingPeakHours, setLoadingPeakHours] = useState(false);

  // Check access and load initial data
  useEffect(() => {
    checkAccess();
  }, [studioId, user]);

  // Load analytics when tab or filters change
  useEffect(() => {
    if (studioId && studioGames.length > 0) {
      loadAnalytics();
    }
  }, [activeTab, selectedGame, dateRange, studioGames]);

  const checkAccess = async () => {
    if (!user) {
      navigate('/user-login');
      return;
    }

    try {
      // Verify user owns this studio
      const { data: studio, error } = await supabase
        .from('gaming_studios')
        .select('id, name, owner_id')
        .eq('id', studioId)
        .single();

      if (error || !studio || studio.owner_id !== user.id) {
        navigate('/');
        return;
      }

      // Load studio's games
      await loadStudioGames();
    } catch (error) {
      console.error('Error checking access:', error);
      navigate('/');
    }
  };

  const loadStudioGames = async () => {
    try {
      const { data: games, error } = await supabase
        .from('games')
        .select('id, title, status')
        .eq('studio_id', studioId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      setStudioGames(games || []);
      setLoading(false);
    } catch (error) {
      console.error('Error loading studio games:', error);
      setLoading(false);
    }
  };

  const loadAnalytics = async () => {
    // Load different analytics based on active tab
    if (activeTab === 'overview' || activeTab === 'cohorts') {
      await loadCohortRetention();
    }

    if (activeTab === 'overview' || activeTab === 'benchmarks') {
      await loadCompetitiveBenchmarks();
    }

    if (activeTab === 'overview' || activeTab === 'journey') {
      await loadPlayerJourney();
    }

    if (activeTab === 'overview' || activeTab === 'peak-hours') {
      await loadPeakHours();
    }
  };

  const loadCohortRetention = async () => {
    setLoadingCohort(true);
    try {
      const data = await studioIntelligenceService.getCohortRetention(
        studioId,
        selectedGame,
        Math.ceil(dateRange / 7) // Convert days to weeks
      );
      setCohortData(data);
    } catch (error) {
      console.error('Error loading cohort retention:', error);
    } finally {
      setLoadingCohort(false);
    }
  };

  const loadCompetitiveBenchmarks = async () => {
    if (!selectedGame && studioGames.length === 0) {
      setBenchmarkData(null);
      return;
    }

    setLoadingBenchmark(true);
    try {
      // Use first game if no specific game selected
      const gameId = selectedGame || studioGames[0]?.id;
      if (gameId) {
        const data = await studioIntelligenceService.getCompetitiveBenchmarks(gameId);
        setBenchmarkData(data);
      }
    } catch (error) {
      console.error('Error loading competitive benchmarks:', error);
    } finally {
      setLoadingBenchmark(false);
    }
  };

  const loadPlayerJourney = async () => {
    if (!selectedGame && studioGames.length === 0) {
      setJourneyData(null);
      return;
    }

    setLoadingJourney(true);
    try {
      const gameId = selectedGame || studioGames[0]?.id;
      if (gameId) {
        const data = await studioIntelligenceService.getPlayerJourney(gameId, dateRange);
        setJourneyData(data);
      }
    } catch (error) {
      console.error('Error loading player journey:', error);
    } finally {
      setLoadingJourney(false);
    }
  };

  const loadPeakHours = async () => {
    if (!selectedGame && studioGames.length === 0) {
      setPeakHoursData(null);
      return;
    }

    setLoadingPeakHours(true);
    try {
      const gameId = selectedGame || studioGames[0]?.id;
      if (gameId) {
        const data = await studioIntelligenceService.getPeakHoursData(gameId, dateRange);
        setPeakHoursData(data);
      }
    } catch (error) {
      console.error('Error loading peak hours:', error);
    } finally {
      setLoadingPeakHours(false);
    }
  };

  const handleRefresh = () => {
    loadAnalytics();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading Studio Intelligence Hub...</p>
        </div>
      </div>
    );
  }

  if (studioGames.length === 0) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-8">
        <div className="max-w-md text-center">
          <Icon name="BarChart3" size={64} className="text-muted-foreground mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-foreground mb-2">No Games Yet</h2>
          <p className="text-muted-foreground mb-6">
            Upload your first game to start seeing powerful analytics insights.
          </p>
          <Button onClick={() => navigate(`/studio/${studioId}/games`)}>
            Upload Your First Game
          </Button>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'LayoutDashboard' },
    { id: 'cohorts', label: 'Retention', icon: 'Calendar' },
    { id: 'benchmarks', label: 'Benchmarks', icon: 'BarChart3' },
    { id: 'journey', label: 'Player Journey', icon: 'TrendingUp' },
    { id: 'peak-hours', label: 'Peak Hours', icon: 'Clock' }
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-background/95 backdrop-blur-md border-b border-border">
        <div className="container mx-auto px-4 lg:px-8 py-4">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <h1 className="text-2xl lg:text-3xl font-bold text-foreground flex items-center gap-3">
                <Icon name="BarChart3" size={32} className="text-primary" />
                Studio Intelligence Hub
                <span className="px-2 py-1 text-xs font-semibold bg-purple-500/20 text-purple-500 rounded-full">
                  EXCLUSIVE
                </span>
              </h1>
              <p className="text-muted-foreground mt-1">
                Advanced analytics intelligence no other platform offers
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              {/* Game Selector */}
              <select
                value={selectedGame || ''}
                onChange={(e) => setSelectedGame(e.target.value || null)}
                className="px-4 py-2 bg-card border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="">All Games</option>
                {studioGames.map((game) => (
                  <option key={game.id} value={game.id}>
                    {game.title}
                  </option>
                ))}
              </select>

              {/* Date Range */}
              <select
                value={dateRange}
                onChange={(e) => setDateRange(parseInt(e.target.value))}
                className="px-4 py-2 bg-card border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value={7}>Last 7 Days</option>
                <option value={30}>Last 30 Days</option>
                <option value={90}>Last 90 Days</option>
              </select>

              {/* Refresh Button */}
              <Button
                variant="outline"
                size="sm"
                iconName="RefreshCw"
                onClick={handleRefresh}
              >
                Refresh
              </Button>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 mt-4 overflow-x-auto pb-2">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm whitespace-nowrap transition-colors ${
                  activeTab === tab.id
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-card text-muted-foreground hover:text-foreground hover:bg-muted'
                }`}
              >
                <Icon name={tab.icon} size={18} />
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <main className="container mx-auto px-4 lg:px-8 py-8">
        {/* Overview Tab - Show all analytics */}
        {activeTab === 'overview' && (
          <div className="space-y-8">
            <CohortRetentionHeatMap data={cohortData} loading={loadingCohort} />
            <CompetitiveBenchmarkChart data={benchmarkData} loading={loadingBenchmark} />
            <PlayerJourneyFunnel data={journeyData} loading={loadingJourney} />
            <PeakHoursHeatMap data={peakHoursData} loading={loadingPeakHours} />
          </div>
        )}

        {/* Cohorts Tab */}
        {activeTab === 'cohorts' && (
          <div>
            <CohortRetentionHeatMap data={cohortData} loading={loadingCohort} />
          </div>
        )}

        {/* Benchmarks Tab */}
        {activeTab === 'benchmarks' && (
          <div>
            <CompetitiveBenchmarkChart data={benchmarkData} loading={loadingBenchmark} />
          </div>
        )}

        {/* Journey Tab */}
        {activeTab === 'journey' && (
          <div>
            <PlayerJourneyFunnel data={journeyData} loading={loadingJourney} />
          </div>
        )}

        {/* Peak Hours Tab */}
        {activeTab === 'peak-hours' && (
          <div>
            <PeakHoursHeatMap data={peakHoursData} loading={loadingPeakHours} />
          </div>
        )}
      </main>

      {/* Footer Info */}
      <div className="border-t border-border bg-card/50 mt-12">
        <div className="container mx-auto px-4 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Icon name="Shield" size={16} />
              <span>All comparisons are anonymous. Your competitors cannot see your metrics.</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-purple-500 font-medium">
              <Icon name="Sparkles" size={16} />
              <span>Exclusive to Solis Games</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
